import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { ApiListScenariosResponse, ApiScenarioResponse } from '../../api/core/models';
import { filter, map, mergeMap, Observable, pipe, take, tap } from 'rxjs';
import { ScenariosService } from '../../api/core/services';
import { selectRouteBoardId } from '../router/router.selectors';
import { injectUntilDestroyed } from '../../functions/inject-functions/inject-until-destroyed';
import { QueryParamsStore } from '../query-params.store';

type ScenariosState = {
  error: Error | HttpErrorResponse | undefined;
  isLoading: boolean;
  scenarios: ApiScenarioResponse[] | undefined;
  selectedScenario: ApiScenarioResponse | undefined;
};

export const scenariosInitialState: ScenariosState = {
  error: undefined,
  isLoading: false,
  scenarios: undefined,
  selectedScenario: undefined,
};

@Injectable({ providedIn: 'root' })
export class ScenariosStore extends signalStore(
  withState(scenariosInitialState),
  withComputed((store) => ({
    hasMultipleScenarios: computed(() => (store.scenarios() ?? []).length > 1),
  })),
  withMethods((store) => {
    const store$ = inject(Store);
    const scenariosService = inject(ScenariosService);
    const queryParamsStore = inject(QueryParamsStore);

    return {
      /**
       * Load scenarios from the API
       */
      loadScenarios: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(() => {
            return store$.select(selectRouteBoardId).pipe(
              filter((routeBoardId) => Boolean(routeBoardId)),
              mergeMap(
                (routeBoardId): Observable<ApiListScenariosResponse> =>
                  scenariosService.listScenarios({ boardId: routeBoardId! })
              ),
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    scenarios: response.scenarios,
                    selectedScenario: response.scenarios.find((scenario) => scenario.isPrimary) ?? undefined,
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error loading scenarios:', error);
                },
              })
            );
          })
        )
      ),
      /**
       * Creates a new scenario. If originScenarioId is provided, the new new scenario will be populated with a copy of the data from the original scenario.
       */
      createScenario: rxMethod<{ name: string; description: string; originScenarioId?: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ name, description, originScenarioId }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((routeBoardId) => Boolean(routeBoardId)),
              take(1),
              mergeMap((routeBoardId) =>
                scenariosService.createScenario({
                  boardId: routeBoardId!,
                  body: {
                    name,
                    description,
                    originScenarioId,
                  },
                })
              ),
              tapResponse({
                next: (response) => {
                  const newScenarios = [...(store.scenarios() ?? []), response];
                  patchState(store, {
                    scenarios: newScenarios,
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error creating scenario:', error);
                },
              })
            )
          )
        )
      ),

      deleteScenario: rxMethod<{ id: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ id }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((routeBoardId) => Boolean(routeBoardId)),
              take(1),
              mergeMap((routeBoardId) =>
                scenariosService.deleteScenario({
                  boardId: routeBoardId!,
                  id: id,
                })
              ),
              tapResponse({
                next: () => {
                  const newScenarios = (store.scenarios() ?? []).filter((scenario) => scenario.id !== id);
                  const selectedScenario = store.selectedScenario();
                  if (selectedScenario?.id === id) {
                    const defaultScenario = newScenarios.find((scenario) => scenario.isPrimary) ?? newScenarios[0];
                    patchState(store, { selectedScenario: defaultScenario });
                  }
                  patchState(store, {
                    scenarios: newScenarios,
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error deleting scenario:', error);
                },
              })
            )
          )
        )
      ),

      updateScenario: rxMethod<{ id: string; name?: string; description?: string; isPrimary?: true }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(({ id, name, description, isPrimary }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((routeBoardId) => Boolean(routeBoardId)),
              take(1),
              mergeMap((routeBoardId) => {
                return scenariosService.updateScenario({
                  boardId: routeBoardId!,
                  id: id,
                  body: {
                    name,
                    description,
                    isPrimary,
                  },
                });
              }),
              tapResponse({
                next: (response) => {
                  const newScenarios = (store.scenarios() ?? []).map((scenario) => {
                    if (scenario.id === id) {
                      const updatedScenario = { ...scenario };
                      if (name) updatedScenario.name = name;
                      if (description) updatedScenario.description = description;
                      if (isPrimary) updatedScenario.isPrimary = isPrimary;
                      return updatedScenario;
                    }
                    if (isPrimary) {
                      return { ...scenario, isPrimary: false };
                    }
                    return scenario;
                  });
                  const data: Partial<ScenariosState> = {
                    scenarios: newScenarios,
                    isLoading: false,
                    error: undefined,
                  };
                  const selectedScenario = store.selectedScenario();
                  const updatedScenario = newScenarios.find((scenario) => scenario.id === selectedScenario?.id);
                  if (selectedScenario && updatedScenario!.isPrimary !== selectedScenario.isPrimary) {
                    data.selectedScenario = updatedScenario;
                  }
                  patchState(store, data);
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error setting scenario as primary:', error);
                },
              })
            )
          )
        )
      ),
      setCurrentScenarioById: rxMethod<string>(
        pipe(
          map((scenarioId): ApiScenarioResponse | undefined =>
            store.scenarios()?.find((scenario) => scenario.id === scenarioId)
          ),
          tap((scenario: ApiScenarioResponse | undefined) => {
            if (scenario) {
              patchState(store, { selectedScenario: scenario });

              // Update the query parameters
              queryParamsStore.setScenarioId(scenario.id);
            }
          })
        )
      ),
    };
  }),
  withHooks({
    onInit: (store) => {
      const untilDestroyed = injectUntilDestroyed();
      // Reset state when board changes
      inject(Store)
        .select(selectRouteBoardId)
        .pipe(untilDestroyed())
        .subscribe(() => {
          patchState(store, scenariosInitialState);
        });
    },
  })
) {
  private _queryParamsStore = inject(QueryParamsStore);

  constructor() {
    super();

    effect(() => {
      const scenarioId = this._queryParamsStore.scenarioId();
      if (scenarioId) {
        this.setCurrentScenarioById(scenarioId);
      }
    });
  }
}
